import React from 'react';
import { useTranslation } from 'react-i18next';
import { getActiveCurrency } from '../Shared/Formatters';

/**
 * AnalyticsKpiStrip — 4 KPI tiles with period-over-period delta.
 *
 * Props:
 *   current — { sales, transactionCount, atv, revenueShareAmount }  (totals)
 *   prior   — same shape, or null when prior period unavailable
 *   loading — boolean (renders skeletons)
 */
export default function AnalyticsKpiStrip({ current, prior, loading }) {
    const { t } = useTranslation();
    const tiles = [
        { key: 'sales',              label: 'Sales',          format: 'money',  hint: 'Net sales' },
        { key: 'transactionCount',   label: 'Transactions',   format: 'int',    hint: 'Tx count' },
        { key: 'atv',                label: 'ATV',            format: 'money',  hint: 'Avg ticket value' },
        { key: 'revenueShareAmount', label: 'Revenue Share',  format: 'money',  hint: 'Concessionaire share' },
    ];

    return (
        <div className="v3-anlx-kpi-strip">
            {tiles.map(tile => {
                const cur = current ? Number(current[tile.key]) || 0 : null;
                const prv = prior   ? Number(prior[tile.key])   || 0 : null;
                const delta = (cur != null && prv != null) ? computeDelta(cur, prv) : null;

                return (
                    <div key={tile.key} className="v3-card v3-anlx-kpi" aria-label={t('{{label}} KPI tile', { label: t(tile.label) })}>
                        <div className="v3-anlx-kpi-label">{t(tile.label)}</div>
                        {loading ? (
                            <div className="v3-anlx-skel tile" />
                        ) : (
                            <>
                                <div className="v3-anlx-kpi-value">
                                    {cur != null ? formatValue(cur, tile.format) : '—'}
                                </div>
                                <div className="v3-anlx-kpi-meta">
                                    {delta && (
                                        <span className={`v3-delta ${delta.dir}`}>
                                            {delta.dir === 'up' ? '↑' : delta.dir === 'down' ? '↓' : '·'} {delta.text}
                                        </span>
                                    )}
                                    <span className="v3-anlx-kpi-prior">
                                        {prv != null ? t('vs {{value}} prior', { value: formatValue(prv, tile.format) }) : ''}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function computeDelta(cur, prv) {
    if (prv === 0 && cur === 0) return { dir: 'neutral', text: '0%' };
    if (prv === 0) return { dir: cur > 0 ? 'up' : 'down', text: '—' };
    const pct = ((cur - prv) / Math.abs(prv)) * 100;
    const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral';
    return { dir, text: `${Math.abs(pct).toFixed(1)}%` };
}

function formatValue(v, kind) {
    if (!Number.isFinite(v)) return '—';
    if (kind === 'int')   return Math.round(v).toLocaleString();
    if (kind === 'money') {
        // Compact when large; prefix the tenant's display currency.
        const cur = getActiveCurrency();
        if (v >= 1_000_000) return `${cur} ${(v / 1_000_000).toFixed(2)}M`;
        if (v >= 10_000)    return `${cur} ${(v / 1_000).toFixed(1)}K`;
        return `${cur} ${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    }
    return String(v);
}
